// M3 concessions, lease rounds and licences (M3_SPEC §7.1-§7.2).
//   sealed_bonus (US OCS, Alaska): rivals ~ Poisson(rivalsMean) value a tract at trueValue × exp(N(0, bidSigma)) and bid
//     bidShade × value (floor minBid); highest bid wins and pays. The winner is whoever overestimated most: the
//     winner's curse (Capen, Clapp & Campbell 1971). The player's own estimate carries its survey σ (landman × 0.7).
//   discretionary (UK/Norway): score = work-programme wells + reputation/50 + state-partner bonus; winning commits the
//     wells within termYears or the undrilled acreage is revoked (no refund).
//   concession / production_sharing: score = bonus / minBid + wells.
// Between rounds: UK/Norway licences and (after the OCS Lands Act) federal Gulf / Alaska tracts are round-only;
// Middle East concessions need reputation and a one-time signature bonus; JV fields come with their state partner.
import { makeTaken } from '../shapes.js';
import { wellsOnField } from '../indexes.js';
import { LEASE_ROUNDS, leaseRoundById as leaseRoundByIdM3 } from '../../data/m3/rules.js';
import { leaseRoundM3Shape } from '../m4/worldHooks.js'; // M4-C: 2000s rounds (LEASE_ROUNDS_M4) in the M3 shape
const leaseRoundById = (id) => leaseRoundByIdM3(id) ?? leaseRoundM3Shape(id); // M4-C
import { M3_LOCAL_PARTNERS } from '../../data/m3/regions.js';
import { hash32, nextFloat, int, range } from '../../core/rng.js';
import { eraDollars, fieldDef, regionDef, yf } from '../defs.js';
import { reservoirsAt } from '../geo.js';
import { spotPrice } from '../../economy/market.js';
import { flagOn } from '../m2util.js';
import { regionState } from '../m2/politics.js';
import { notify, spend, fmt$, clamp } from '../util.js';
import { econState, m3On, bumpStat } from './econ.js';
import { m3PriceMult } from './prices.js';

export const BID_WINDOW_DAYS = 30;
export const DRY_TRACT_VALUE = 0.1;       // a dry tract's value as a share of the field's average wet tract (deeper horizons, farm-outs)
export const DISCOUNTED_DAYS = 1100;      // discounted barrels per bbl/d of initial rate (≈ 3 years at full rate)
export const MARGIN_SHARE = 0.55;         // of the after-royalty price: opex, transport and taxes take the rest
export const DEV_HAIRCUT = 0.6;           // development cost & risk
export const LANDMAN_SIGMA_MULT = 0.7;
export const MISSED_PAYMENT_REP = -5;
export const REVOKE_REP = -5;
const RIVALS = ['Humble Oil', 'Shell', 'Gulf Oil', 'Texaco', 'Mobil', 'Phillips', 'Continental', 'Kerr-McGee', 'Union Oil', 'Sun Oil', 'Tenneco', 'Atlantic Richfield', 'BP', 'Amoco', 'Getty', 'Occidental', 'Total', 'Elf', 'Agip', 'Hamilton Brothers'];

export function roundsState(state) { state.rounds ??= {}; return state.rounds; }
const tractId = (fid, idx) => `${fid}:${idx}`;
const hasRole = (state, role) => Object.values(state.crew).some((c) => c.role === role && c.status === 'active');

// ---- value model -------------------------------------------------------------------------------
function wetValue(state, fdef, plot, royalty, qiFrac = null) {
  const res = reservoirsAt(fdef, plot);
  if (!res.length) return 0;
  const r = res[0];
  const [lo, hi] = r.qiRange ?? [50, 500];
  const qi = lo + (hi - lo) * (qiFrac ?? plot.truth?.qiRoll ?? 0.5);
  const price = Math.max(0, spotPrice(state)) * m3PriceMult(state, fdef.id);
  return qi * DISCOUNTED_DAYS * price * (1 - royalty) * MARGIN_SHARE * DEV_HAIRCUT;
}
function avgWetValue(state, fdef, plots, royalty) {
  let a = 0, n = 0;
  for (const p of plots) if ((p.truth?.prospectIdx ?? -1) >= 0) { a += wetValue(state, fdef, p, royalty, 0.5); n++; }
  return n ? a / n : 0;
}
// Expected NPV of a tract from its hidden truth at today's price (era $).
export function tractTrueValue(state, round, fieldId, idx) {
  const fdef = fieldDef(fieldId);
  const plots = state.fields[fieldId].plots;
  const p = plots[idx];
  const roy = round.royalty ?? 0.125;
  return (p.truth?.prospectIdx ?? -1) >= 0 ? wetValue(state, fdef, p, roy) : DRY_TRACT_VALUE * avgWetValue(state, fdef, plots, roy);
}
// What the player can know: own survey estimate (or the public one) × average wet value, with σ.
export function tractEstimate(state, round, fieldId, idx) {
  const fdef = fieldDef(fieldId);
  const plots = state.fields[fieldId].plots;
  const p = plots[idx];
  const est = p.survey?.est ?? p.pubEst ?? 0.3;
  let sigma = p.survey?.sigma ?? 0.45;
  if (hasRole(state, 'landman')) sigma *= LANDMAN_SIGMA_MULT;
  const wet = avgWetValue(state, fdef, plots, round.royalty ?? 0.125);
  const v = wet * (DRY_TRACT_VALUE + (1 - DRY_TRACT_VALUE) * clamp(est, 0, 1));
  return { value: v, low: wet * clamp(est - sigma, 0, 1), high: wet * clamp(est + sigma, 0, 1), sigma, est };
}

export const minBid = (state, round) => eraDollars(state, round.minBidPerPlot, round.baseYear ?? Math.floor(round.year));

// ---- pure sealed-bid model (also used by the acceptance test) ----------------------------------------
// rng: () => U[0,1). Returns { winner: index (-1 none), bid, bids[], estimates[] } for one tract.
export function sealedBids(trueValue, round, rng, nRivals, minBidAmt = 0) {
  const sigma = round.bidSigma ?? 0.6, shade = round.bidShade ?? 0.4;
  const bids = [], estimates = [];
  for (let i = 0; i < nRivals; i++) {
    const u1 = Math.max(1e-12, rng()), u2 = rng();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const est = trueValue * Math.exp(z * sigma);
    estimates.push(est);
    const b = shade * est;
    bids.push(b >= minBidAmt ? b : 0);
  }
  let w = -1;
  bids.forEach((b, i) => { if (b > 0 && (w < 0 || b > bids[w])) w = i; });
  return { winner: w, bid: w >= 0 ? bids[w] : 0, bids, estimates };
}
export function poisson(mean, rng) {
  const L = Math.exp(-mean);
  let k = 0, p = 1;
  do { k++; p *= rng(); } while (p > L && k < 50);
  return k - 1;
}

// ---- opening a round ----------------------------------------------------------------------------
export function openRound(state, roundId) {
  const round = leaseRoundById(roundId);
  if (!round || !m3On(state)) return null;
  const R = roundsState(state);
  if (R[roundId]) return R[roundId];
  const cands = [];
  for (const fid of round.fieldIds) {
    const f = state.fields[fid];
    if (!f) continue;
    f.plots.forEach((p, idx) => {
      if (p.owned || p.wellId || p.taken) return;
      // nominated tracts: what the industry's seismic likes (public estimate + structure), deterministic jitter
      const jitter = (hash32(state.seed ?? 0, `${roundId}:${fid}:${idx}`) % 1000) / 1000;
      cands.push({ id: tractId(fid, idx), fieldId: fid, idx, score: (p.pubEst ?? 0.3) + ((p.truth?.prospectIdx ?? -1) >= 0 ? 0.3 : 0) + 0.45 * jitter });
    });
  }
  cands.sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
  const tracts = cands.slice(0, round.tracts).map(({ id, fieldId, idx }) => ({ id, fieldId, idx }));
  const rec = { id: roundId, openDay: state.day, closesDay: state.day + BID_WINDOW_DAYS, tracts, bids: {}, results: [], closedDay: null, kind: round.kind };
  R[roundId] = rec;
  if (tracts.length) notify(state, 'leaseRound', { roundId, phase: 'open', tracts: tracts.length, pause: true, level: 'major', text: `${round.name}: ${tracts.length} tracts on offer. ${round.kind === 'discretionary' ? 'Apply with a work programme' : 'Sealed bids'} within ${BID_WINDOW_DAYS} days.` });
  return rec;
}

function resolveTract(rec, tract) { return typeof tract === 'number' ? rec.tracts[tract] : rec.tracts.find((t) => t.id === tract); }

export function placeBidImpl(state, roundId, tract, amount, { wells = null } = {}) {
  const round = leaseRoundById(roundId);
  const rec = state.rounds?.[roundId];
  if (!round || !rec) return { ok: false, msg: 'That round is not open.' };
  if (rec.closedDay != null || state.day >= rec.closesDay) return { ok: false, msg: 'Bidding has closed.' };
  const t = resolveTract(rec, tract);
  if (!t) return { ok: false, msg: 'No such tract in this round.' };
  if (round.kind === 'discretionary') return applyLicenceImpl(state, roundId, tract, wells ?? amount);
  const amt = Math.round(Number(amount) || 0);
  const min = minBid(state, round);
  if (amt < min) return { ok: false, msg: `The minimum bid is ${fmt$(min)}.` };
  const w = round.kind === 'sealed_bonus' ? 0 : clamp(Math.round(Number(wells ?? round.workCommitmentWells ?? 0)), 0, 6);
  const others = Object.entries(rec.bids).filter(([k]) => k !== t.id).reduce((a, [, b]) => a + (b.amount ?? 0), 0);
  if (state.company.cash < others + amt) return { ok: false, msg: `Your bids would total ${fmt$(others + amt)}; you have ${fmt$(state.company.cash)}.` };
  rec.bids[t.id] = { amount: amt, wells: w, day: state.day };
  return { ok: true, msg: `Sealed bid of ${fmt$(amt)} lodged for tract ${t.idx + 1} (${fieldDef(t.fieldId).name}).` };
}

export function applyLicenceImpl(state, roundId, tract, wells) {
  const round = leaseRoundById(roundId);
  const rec = state.rounds?.[roundId];
  if (!round || !rec) return { ok: false, msg: 'That round is not open.' };
  if (round.kind !== 'discretionary') return placeBidImpl(state, roundId, tract, wells);
  if (rec.closedDay != null || state.day >= rec.closesDay) return { ok: false, msg: 'Applications have closed.' };
  const t = resolveTract(rec, tract);
  if (!t) return { ok: false, msg: 'No such block in this round.' };
  const w = Math.round(Number(wells) || 0);
  if (w < Math.max(1, round.workCommitmentWells ?? 1)) return { ok: false, msg: `Commit at least ${Math.max(1, round.workCommitmentWells ?? 1)} well${(round.workCommitmentWells ?? 1) > 1 ? 's' : ''}.` };
  if (w > 6) return { ok: false, msg: 'Six wells is the most a block can absorb.' };
  const fee = minBid(state, round);
  const others = Object.entries(rec.bids).filter(([k]) => k !== t.id).length * fee;
  if (state.company.cash < others + fee) return { ok: false, msg: `The licence fee is ${fmt$(fee)} per block.` };
  rec.bids[t.id] = { amount: fee, wells: w, day: state.day };
  return { ok: true, msg: `Application for block ${t.idx + 1} (${fieldDef(t.fieldId).name}) with a ${w}-well work programme.` };
}

export function withdrawBidImpl(state, roundId, tract) {
  const rec = state.rounds?.[roundId];
  const t = rec ? resolveTract(rec, tract) : null;
  if (!t || !rec.bids[t.id] || rec.closedDay != null) return { ok: false, msg: 'No open bid there.' };
  delete rec.bids[t.id];
  return { ok: true, msg: 'Bid withdrawn.' };
}

// ---- closing a round --------------------------------------------------------------------------------
const rngOf = (state) => () => nextFloat(state);
function award(state, round, t, amount, wells) {
  const p = state.fields[t.fieldId].plots[t.idx];
  p.owned = true; p.leaseDay = state.day; p.leaseExpires = state.day + (round.termYears ?? 5) * 365; p.leasePaid = amount;
  p.royalty = round.royalty ?? 0.125; p.roundWon = round.id; p.taken = null;
  if (round.kind !== 'sealed_bonus') p.licence = { roundId: round.id, wells, statePartnerShare: round.statePartnerShare ?? 0 };
}
function rivalTakes(state, t, name) {
  const p = state.fields[t.fieldId].plots[t.idx];
  p.taken = makeTaken({ by: name, day: state.day, drillDay: state.day + int(state, 90, 540), well: null, roundWon: true });
}

export function closeRound(state, roundId) {
  const round = leaseRoundById(roundId);
  const rec = state.rounds?.[roundId];
  if (!round || !rec || rec.closedDay != null) return null;
  rec.closedDay = state.day;
  const rng = rngOf(state);
  const min = minBid(state, round);
  let won = 0, paid = 0, wellsCommitted = 0;
  const wonTracts = [];
  for (const t of rec.tracts) {
    const p = state.fields[t.fieldId]?.plots?.[t.idx];
    if (!p || p.owned || p.wellId) continue;
    const mine = rec.bids[t.id];
    const nR = poisson(round.rivalsMean ?? 3, rng);
    const name = () => RIVALS[int(state, 0, RIVALS.length - 1)];
    let res;
    if (round.kind === 'sealed_bonus') {
      const tv = tractTrueValue(state, round, t.fieldId, t.idx);
      const sb = sealedBids(tv, round, rng, nR, min);
      const best = sb.winner >= 0 ? sb.bid : 0;
      const you = mine && mine.amount > best;
      res = { tractId: t.id, winner: you ? 'you' : sb.winner >= 0 ? name() : null, bid: Math.round(you ? mine.amount : best), rivals: nR, you: mine?.amount ?? null, secondBid: Math.round(you ? best : 0) };
      if (you) {
        if (!spend(state, mine.amount, 'leases')) { res.winner = null; res.forfeit = true; state.company.reputation = clamp(state.company.reputation + MISSED_PAYMENT_REP, 0, 100); }
        else { award(state, round, t, mine.amount, 0); won++; paid += mine.amount; wonTracts.push(t); }
      } else if (res.winner) rivalTakes(state, t, res.winner);
    } else {
      // discretionary / concession / PSC: scored applications
      const rep = state.company.reputation;
      const disc = round.kind === 'discretionary';
      const myScore = mine ? (disc ? mine.wells + rep / 50 + (round.statePartnerShare ? 0.5 : 0) : mine.amount / min + mine.wells) : -1;
      let best = -1;
      for (let i = 0; i < nR; i++) {
        const s = disc ? range(state, 1, 3) + range(state, 30, 90) / 50 : range(state, 1, 3) + int(state, 0, 3);
        if (s > best) best = s;
      }
      const you = mine && myScore > best;
      res = { tractId: t.id, winner: you ? 'you' : nR > 0 ? name() : null, bid: mine && you ? mine.amount : null, rivals: nR, you: mine ? { wells: mine.wells, amount: mine.amount, score: Math.round(myScore * 100) / 100 } : null, bestRivalScore: Math.round(best * 100) / 100 };
      if (you) {
        if (!spend(state, mine.amount, 'leases')) { res.winner = null; res.forfeit = true; }
        else { award(state, round, t, mine.amount, mine.wells); won++; paid += mine.amount; wellsCommitted += mine.wells; wonTracts.push(t); }
      } else if (res.winner) rivalTakes(state, t, res.winner);
    }
    rec.results.push(res);
  }
  // licence commitments (one licence per round and field)
  if (round.kind !== 'sealed_bonus' && wonTracts.length) {
    const E = econState(state);
    const byField = {};
    for (const t of wonTracts) (byField[t.fieldId] ??= []).push(t);
    for (const [fid, ts] of Object.entries(byField)) {
      const req = ts.reduce((a, t) => a + (rec.bids[t.id]?.wells ?? 0), 0);
      E.licences[`${roundId}:${fid}`] = { roundId, fieldId: fid, plots: ts.map((t) => t.idx), wellsRequired: req, awardDay: state.day, dueDay: state.day + (round.termYears ?? 5) * 365, status: 'active' };
    }
  }
  if (won) bumpStat(state, 'roundsWon', won);
  const anyBid = Object.keys(rec.bids).length > 0;
  const text = won ? `${round.name}: you won ${won} tract${won > 1 ? 's' : ''}${paid > 0 ? ` for ${fmt$(paid)}` : ''}${wellsCommitted ? `, committing ${wellsCommitted} well${wellsCommitted > 1 ? 's' : ''}` : ''}.` : anyBid ? `${round.name}: outbid on every tract you went for.` : `${round.name}: the tracts went to others.`;
  notify(state, 'leaseRound', { roundId, phase: 'results', won, paid, concessionWon: won > 0 && (paid >= 1e6 || round.kind !== 'sealed_bonus'), pause: anyBid, level: won ? 'major' : 'info', text });
  return rec;
}

// ---- licences: work commitments ------------------------------------------------------------------
export function tickLicences(state) {
  const E = state.m3econ;
  if (!E) return;
  for (const L of Object.values(E.licences)) {
    if (L.status !== 'active') continue;
    const drilled = wellsOnField(state, L.fieldId).filter((w) => w.spudDay != null && w.spudDay >= L.awardDay && !w.ownerOperatorId).length;
    L.wellsDrilled = drilled;
    if (drilled >= L.wellsRequired) { L.status = 'fulfilled'; continue; }
    if (state.day < L.dueDay) continue;
    L.status = 'revoked';
    const f = state.fields[L.fieldId];
    let n = 0;
    for (const idx of L.plots) { const p = f?.plots?.[idx]; if (p?.owned && !p.wellId) { p.owned = false; p.leaseExpires = null; p.leaseDay = null; p.licence = null; n++; } }
    state.company.reputation = clamp(state.company.reputation + REVOKE_REP, 0, 100);
    notify(state, 'news', { kind: 'lease', fieldId: L.fieldId, level: 'warn', text: `Licence revoked at ${fieldDef(L.fieldId)?.name}: you drilled ${drilled} of ${L.wellsRequired} committed wells. ${n} block${n === 1 ? '' : 's'} returned to the state, no refund.` });
  }
}

// rounds: open on their day (news effect or date), close after the bid window
export function tickRounds(state) {
  for (const r of LEASE_ROUNDS) if (r.day === state.day && !state.rounds?.[r.id]) openRound(state, r.id);
  for (const rec of Object.values(state.rounds ?? {})) if (rec.closedDay == null && state.day >= rec.closesDay) closeRound(state, rec.id);
  // well.roundWon for the achievement
  for (const w of Object.values(state.wells)) {
    if (w.roundWon || w.spudDay == null) continue;
    const p = state.fields[w.fieldId]?.plots?.[w.plot];
    if (p?.roundWon) w.roundWon = p.roundWon;
  }
}

// ---- concession gates for leasePlot -----------------------------------------------------------------
export function concessionOf(fdef) { return fdef?.concession ?? null; }

// Reason the plot cannot be leased outside a round, or null.
export function roundOnlyReason(state, fieldId) {
  const fdef = fieldDef(fieldId);
  const c = concessionOf(fdef);
  if (!c || !m3On(state)) return null;
  if (fdef.serviceOnly || c.kind === 'service_only') return `${fdef.name} belongs to ${fdef.serviceOnly?.operator ?? c.stateCompany ?? 'the state company'}: foreigners can only charter rigs to it.`;
  if (c.kind === 'licence') return `${regionDef(fdef.regionId)?.name ?? 'This sector'}: acreage is awarded only in licensing rounds.`;
  if (c.kind === 'lease' && c.bidRound) {
    const federal = fdef.regionId === 'gulf_of_mexico' ? (flagOn(state, 'ocsLandsAct') || fieldId === 'deep_gulf') : true;
    if (federal) return `${fdef.name}: tracts are offered only in ${fdef.regionId === 'alaska' ? 'state' : 'federal'} lease sales.`;
  }
  return null;
}

export const signatureBonus = (state, c) => (c?.signatureBonus ? eraDollars(state, c.signatureBonus, c.bonusYear ?? 1946) : 0);
export function concessionSigned(state, fieldId) { return !!state.m3econ?.concessions?.[fieldId]; }

// survey.leaseImpl hook (price = the lease price about to be charged): refuse reason, or null; signs the
// concession (charging the bonus) when the first lease needs it.
export function m3LeaseGate(state, fieldId, price) {
  if (!m3On(state)) return null;
  const why = roundOnlyReason(state, fieldId);
  if (why) return why;
  const fdef = fieldDef(fieldId);
  const c = concessionOf(fdef);
  if (c?.kind === 'concession' && c.signatureBonus && !concessionSigned(state, fieldId)) {
    const r = acceptConcessionImpl(state, fieldId, price);
    if (!r.ok) return r.msg;
  }
  return null;
}

// After a lease (round award or leasePlot): JV partner and state-partner shares.
export function m3AfterLease(state, fieldId) {
  if (!m3On(state)) return;
  const fdef = fieldDef(fieldId);
  if (concessionOf(fdef)?.kind !== 'jv') return;
  const rid = fdef.regionId;
  const p = (M3_LOCAL_PARTNERS[rid] ?? []).find((x) => yf(state) >= x.from && yf(state) < x.until);
  const reg = regionState(state, rid);
  if (p && !reg.partner) { reg.partner = p.id; notify(state, 'news', { kind: 'politics', regionId: rid, text: `Joint venture: ${p.name} takes ${Math.round(p.revenueShare * 100)}% of revenue in ${regionDef(rid)?.name}.` }); }
}

export function acceptConcessionImpl(state, fieldId, reserve = 0) {
  const fdef = fieldDef(fieldId);
  const c = concessionOf(fdef);
  if (!fdef || !c) return { ok: false, msg: 'No concession terms here.' };
  if (!m3On(state)) return { ok: false, msg: 'Concession terms start in 1946.' };
  if (concessionSigned(state, fieldId)) return { ok: false, msg: 'You already hold the concession.' };
  if (c.kind !== 'concession') return { ok: false, msg: `${fdef.name} is not a negotiated concession.` };
  const why = roundOnlyReason(state, fieldId);
  if (why) return { ok: false, msg: why };
  if ((c.minReputation ?? 0) > state.company.reputation) return { ok: false, msg: `${c.stateCompany ?? 'The government'} deals only with established companies: reputation ${c.minReputation} needed (you have ${Math.round(state.company.reputation)}).` };
  const bonus = signatureBonus(state, c);
  if (state.company.cash < bonus + reserve) return { ok: false, msg: `The signature bonus is ${fmt$(bonus)}${reserve ? ` (plus ${fmt$(reserve)} for the lease)` : ''}.` };
  if (bonus > 0) spend(state, bonus, 'leases');
  econState(state).concessions[fieldId] = { day: state.day, bonus };
  return { ok: true, msg: `Concession signed for ${fdef.name}${bonus ? `: ${fmt$(bonus)} signature bonus paid` : ''}.` };
}

// ---- view ----------------------------------------------------------------------------------------
export function leaseRoundView(state, roundId = null) {
  const recs = Object.values(state.rounds ?? {});
  const one = (rec) => {
    const round = leaseRoundById(rec.id);
    return {
      id: rec.id, name: round.name, kind: round.kind, regionId: round.regionId, open: rec.closedDay == null, openDay: rec.openDay, closesDay: rec.closesDay,
      daysLeft: rec.closedDay == null ? Math.max(0, rec.closesDay - state.day) : 0, minBid: minBid(state, round), royalty: round.royalty, termYears: round.termYears,
      workCommitmentWells: round.workCommitmentWells, statePartnerShare: round.statePartnerShare ?? 0, rivalsMean: round.rivalsMean,
      tracts: rec.tracts.map((t, i) => ({ i, ...t, field: fieldDef(t.fieldId)?.name, estimate: tractEstimate(state, round, t.fieldId, t.idx), bid: rec.bids[t.id] ?? null, result: rec.results.find((r) => r.tractId === t.id) ?? null })),
      results: rec.results.map((r) => ({ ...r })), totalBid: Object.values(rec.bids).reduce((a, b) => a + (b.amount ?? 0), 0),
    };
  };
  if (roundId) { const rec = state.rounds?.[roundId]; return rec ? one(rec) : null; }
  return {
    open: recs.filter((r) => r.closedDay == null).map(one), past: recs.filter((r) => r.closedDay != null).slice(-8).map(one),
    licences: Object.values(state.m3econ?.licences ?? {}).map((L) => ({ ...L, field: fieldDef(L.fieldId)?.name, daysLeft: Math.max(0, L.dueDay - state.day) })),
    concessions: Object.entries(state.m3econ?.concessions ?? {}).map(([fid, c]) => ({ fieldId: fid, ...c })),
  };
}

// For the field view: lease rules in force (UI badge / tooltip).
export function concessionView(state, fieldId) {
  const fdef = fieldDef(fieldId);
  const c = concessionOf(fdef);
  if (!c) return null;
  return { ...c, signed: concessionSigned(state, fieldId), bonus: signatureBonus(state, c), roundOnly: roundOnlyReason(state, fieldId), repOk: (c.minReputation ?? 0) <= state.company.reputation };
}

